import { AKMeta, AppStateItem, ChatMessageItem, ChatMessageItemTypes, ChatMessageUpdate, ChatMessageUpdateTypes, ContentPart, ContentPartTypes, ErrorItem, MessageItem } from "@aidkitorg/types/lib/convoai/conversation";
import React, { Reducer, useReducer, PropsWithChildren } from "react";

export type ChatPhase = 'unknown' | 'listening' | 'speaking' | 'thinking' | 'initializing';

export type ControlSignal =
  { type: 'clear_chat' }
  | { type: 'update_phase', phase: ChatPhase }
  | { type: 'update_context', key: string, value: (MessageItem | AppStateItem) }
  ;

const defaultConversationState: ChatConversationContextType = {
  messages: [],
  errors: [],
  toolCalls: new Set<number>(),
  phase: 'unknown' as ChatPhase,
  dispatch: () => { console.warn('no chat conversation context setup, ignoring dispatch') }
}

export type ChatConversationContextType = {

  phase: ChatPhase,

  /**
   * The running chat history of the conversation so far
   */
  messages: ChatMessageItem[],

  context?: Map<string, (MessageItem | AppStateItem)>,

  /**
   * The most recently updated chat message item
   */
  latestUpdatedItem?: ChatMessageItem,

  /**
    * The most recent message sent to the conversation
    */
  latestMessage?: { message: ChatMessageItem, index: number },

  /**
   * Any tool calls that are actively unfulfilled.
   * These values correspond to the related indicies in the `message` field.
   */
  toolCalls?: Set<number>,

  /**
   * Any errors that have occurred during the chat.
   * Any of these may cause the chat to terminate/restart.
   */
  errors?: ErrorItem[],

  dispatch: React.Dispatch<ChatMessageUpdate | ControlSignal>
};

export const ChatConversationContext = React.createContext<ChatConversationContextType>(defaultConversationState);
export const ChatConversationProvider = ({ children }: PropsWithChildren) => {
  const [state, dispatch] = useReducer(ChatMessageReducer, {
    list: [],
    map: new Map(),
    errors: [],
    toolCalls: new Set<number>(),
    phase: 'unknown',
  });

  const { list, ...rest } = state;
  const conversationState = {
    messages: list,
    dispatch,
    ...rest
  };

  return <ChatConversationContext.Provider value={conversationState}>
    {children}
  </ChatConversationContext.Provider>
}

export type ChatState = {
  map: Map<string, number>,
  toolCalls: Set<number>,
  list: ChatMessageItem[],
  errors?: ErrorItem[],
  latestUpdatedItem?: ChatMessageItem,
  latestMessage?: { message: ChatMessageItem, index: number },

  /**
    * Shows the current turn of the chat.
    * "listening" indicates its waiting for input (highly correlated to ready flag)
    * "speaking" indicates the assistant is currently replying
    * "thinking" indicates the assistant is constructing a reply
    */
  phase: ChatPhase
};

/**
 * This reducer drives the chat state in its entirety (audio and text).
 */
export const ChatMessageReducer: Reducer<ChatState, ChatMessageUpdate | ControlSignal> =
  ({ map, list, toolCalls, errors, latestUpdatedItem, latestMessage, phase }, action) => {

    function updateItemMeta(itemId: string, update: Partial<AKMeta>) {
      const itemIndex = map.get(itemId);
      if (typeof itemIndex === 'number') {
        const itemToUpdate = list[itemIndex];
        itemToUpdate.meta = {
          time: Date.now(),
          ...itemToUpdate.meta,
          ...update
        };
        latestUpdatedItem = itemToUpdate;
      }
    }

    function upsertItem<T extends ChatMessageUpdate, E = T['type'] extends ChatMessageItem['type'] ? ChatMessageItemTypes[T['type']] : ChatMessageItem>(
      id: string,
      item: T,
      merge: (item: E, update: T) => E): [number, E] {

      const isUpdate = map.has(id);
      if (!isUpdate) {
        map.set(id, list.length);
      }

      const index = map.get(id)!;

      const existing = list[index] ?? {};

      const updatedItem = merge(existing as Parameters<typeof merge>[0], item);
      if (isUpdate) {
        latestUpdatedItem = updatedItem as ChatMessageItem;
      }

      (list[index] as E) = {
        id,
        ...updatedItem
      };

      return [index, list[index] as E];
    }

    function appendError(error: ErrorItem) {
      return (errors || []).concat(error);
    }

    switch (action.type) {

      case "update_phase":
        phase = action.phase;
        break;

      case "clear_chat":
        return {
          version: 0,
          map: new Map(),
          list: [],
          toolCalls: new Set(),
          phase: 'unknown'
        };

      case "update_meta":
        upsertItem(action.id, action, (existing, update) => ({
          id: update.id,
          role: 'user' as 'user',
          content: [],
          ...existing,
          meta: {
            time: Date.now(),
            ...existing.meta,
            ...update.meta,
          }
        }));
        break;

      case "content_delta":
        upsertItem<ChatMessageUpdateTypes['content_delta'], MessageItem>(action.id, action, (existing, update) => {

          existing.content[update.index] =
            update.overwrite ?
              update.content :
              { type: 'text', content: ((existing.content as ContentPartTypes['text'][])?.[update.index]?.content || '') + (update.content as ContentPartTypes['text']).content };

          return existing;
        });
        break;

      case "appState":
        // Don't do anything for appState, for now.
        break;
      case "message":
        upsertItem(action.id, action, (existing, update) => {

          const content: ContentPart[] = [];
          const contentSize = Math.max(existing.content?.length || 0, update.content?.length || 0);
          for (let i = 0; i < contentSize; i++) {
            content[i] = {
              type: 'text',
              content: (update.content?.[i] as ContentPartTypes['text'])?.content || (existing.content?.[i] as ContentPartTypes['text'])?.content || ''
            };
          }

          return {
            ...existing,
            ...update,
            content,
            meta: {
              fromURL: window.location.pathname,
              ...existing.meta,
              ...(update.meta ?? {}),
              time: existing.meta?.time ?? update.meta?.time ?? Date.now(),
            }
          };
        });
        break;
      case "tool_call":
        const [itemIndex, updated] = upsertItem(action.id, action, (existing, update) => {
          return {
            ...existing,
            ...update,
            name: update.name || existing.name,
            arguments: update.arguments || existing.arguments,
            callId: update.callId || existing.callId,
            meta: {
              time: Date.now(),
              ...existing.meta,
              ...(update.meta ?? {})
            }
          };
        });
        if (updated.name && updated.arguments && updated.meta?.phase !== 'done') {
          map.set(updated.callId, itemIndex);
          toolCalls.add(itemIndex);
          // prevents additional calls from happening for the same tool call, 
          // mostly for after restoring from a previous conversation.
          updateItemMeta(action.id, {
            phase: 'done'
          });
        }
        break;
      case "tool_call_output":
        upsertItem(action.id, action, (existing, update) => {
          return {
            ...existing,
            ...update,
            output: update.output || existing.output,
            callId: update.callId || existing.callId,
            meta: {
              time: Date.now(),
              ...existing.meta,
              ...(update.meta ?? {})
            }
          };
        });
        const index = map.get(action.callId);
        if (typeof index === 'number') {
          map.delete(action.callId);
          toolCalls.delete(index);
        }
        break;
      case "error":
        errors = appendError(action);
        break;
      case "remove_item": {
        const index = map.get(action.id);
        if (typeof index === 'number') {
          map.delete(action.id);
          list.splice(index, 1);
        }
        break;
      }
      case "update_item": {
        upsertItem(action.id, action.with, (_, update) => update);
        break;
      }
    }

    // only update if we've added new items.
    // prevents unneeded re-rendering every time there is an update,
    // for watchers of the latestMessage
    const lastListIndex = list.length - 1;
    if(lastListIndex < 0) {
      latestMessage = undefined;
    }
    else if(latestMessage?.index !== lastListIndex) {
      latestMessage = {
        index: lastListIndex,
        message: list[lastListIndex]
      };
    }

    return {
      map: new Map(map),
      list: Array.from(list),
      toolCalls: new Set(toolCalls),
      errors,
      latestUpdatedItem,
      latestMessage,
      phase
    };
  };
